import React, { RefObject, useImperativeHandle, useRef, useState } from 'react'
import moment from 'moment'
import { createStyles, CSSProperties, withStyles } from '@mui/styles'
import {
    Table, TableBody, TableCell, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Typography
} from '@mui/material'
import { t } from './../../infrastructure/i18nextHelper'
import { defaultStyles, dialogCell, defaultColors } from '../../infrastructure/materialUiThemeProvider'
import { api } from '../../infrastructure/api'
import { Button } from '../common/customComponents'
import { MovementListItem, MovementStatus } from './stockModels'
import { Line, roundedSum } from './stockProjectionDetailDialog'
import { StatusIcon } from './_movementIcon'
import { ErrorOutlineOutlined } from '@mui/icons-material'
import { UserSettingsContextContainer } from '../../infrastructure/shell/userSettings/userSettingsStore'

export type PurchaseDealDetailArgs = {
    date: string
    productIds: string[]
    dutyStatuss: string[]
    companys: string[]
    movements: MovementListItem[]
}

type PurchaseDealDetail = {
    companys: string
    sites: string
    products: string
    dutyStatuss: string
    values: MovementDealDetail[]
}

type MovementDealDetail = {
    movementId: string
    movementReference: string | null
    movementQuantity: number | null
    movementStatus: MovementStatus
    deal: DealDetail | null
}

type DealDetail = {
    dealId: string
    pricingType: string
    site: string | null
    pricingFrom: string | null
    pricingTo: string | null
    fixedPriceDate: string | null
    fixedPrice: number | null
    triggerPriceDate: string | null
    triggerPrice: number | null
    premium: number | null
}

let getStockDetailTrad = (text: string) => t('stock.stockProjectionDetailsDialog.' + text)
let getPurchaseDetailTrad = (text: string) => t('stock.purchaseDealDetailsDialog.' + text)

function PurchaseDetailDialog(props) {
    let settings = UserSettingsContextContainer.useContainer()
    let [openDialog, setOpenDialog] = useState<boolean>(false)
    let [purchaseDealDetails, setPurchaseDealDetails] = useState<PurchaseDealDetail | null>(null)
    let [date, setDate] = useState<string | null>(null)

    dialogRef = useRef<{ open: (args: PurchaseDealDetailArgs) => void }>(null)

    useImperativeHandle(dialogRef, () => ({
        open: async (args: PurchaseDealDetailArgs) => {
            setDate(args.date)
            await fetchDetails(args)
            setOpenDialog(true)
        }
    }))

    let fetchDetails = async (args: PurchaseDealDetailArgs) => {
        let result = await api.get<PurchaseDealDetail>(`stock/movement/searchPurchaseDealDetails?${prepareQuery()}`)

        let movementDetails = result?.values || []
        movementDetails = movementDetails.map(detail => {
            let movement = args.movements.find(m => m.id === detail.movementId)
            if (movement) {
                return {
                    ...detail,
                    movementReference: movement.reference || detail.movementReference,
                    movementQuantity: movement.volume || detail.movementQuantity,
                    movementStatus: movement.movementStatus !== null ? movement.movementStatus : MovementStatus.Unknown
                }
            }
            return detail
        })

        setPurchaseDealDetails({ ...result, values: movementDetails })

        function prepareQuery() {
            let query = ''
            args.companys?.map(x => query = query + `companys=${x}&`)
            args.dutyStatuss?.map(x => query = query + `dutystatuss=${x}&`)
            args.productIds?.map(x => query = query + `productIds=${x}&`)
            args.movements?.map(x => query = query + `movementIds=${x.id}&`)
            return query
        }
    }

    let close = () => {
        setPurchaseDealDetails(null)
        setOpenDialog(false)
    }

    let dateFormat = settings.getDateFormat()

    return (
        <Dialog open={openDialog}
            onClose={close}
            scroll='paper'>
            <DialogTitle>{getPurchaseDetailTrad('title')}</DialogTitle>
            <DialogContent dividers>
                <div>
                    <Line classes={props.classes}
                        title={getStockDetailTrad('date')}
                        value={formatDate(date, dateFormat)} />
                    <Line classes={props.classes}
                        title={getStockDetailTrad('products')}
                        value={purchaseDealDetails?.products || ''} />
                    <Line classes={props.classes}
                        title={getStockDetailTrad('dutyStatuss')}
                        value={purchaseDealDetails?.dutyStatuss || ''} />
                    <Line classes={props.classes}
                        title={getStockDetailTrad('companys')}
                        value={purchaseDealDetails?.companys || ''} />
                    <Line classes={props.classes}
                        title={getPurchaseDetailTrad('destinations')}
                        value={purchaseDealDetails?.sites || ''} />
                    <DetailTable classes={props.classes} data={purchaseDealDetails?.values || []} dateFormat={dateFormat} />
                </div>
            </DialogContent>
            <DialogActions>
                <Button className={props.classes.closeButton}
                    label={t('history.close')}
                    onClick={() => setOpenDialog(false)}
                    color='primary' />
            </DialogActions>
        </Dialog>
    );
}

let formatDate = (dateString: string | null | undefined, dateFormat: string) =>
    !!dateString ? moment(dateString).format(dateFormat) : ''

type DetailTableProps = {
    classes: any
    data: MovementDealDetail[]
    dateFormat: string
}

function DetailTable(props: DetailTableProps) {
    let showTriggerCols = props.data.some(x => x.deal?.pricingType === 'Trigger')
    let showFixedCols = props.data.some(x => x.deal?.pricingType === 'Fixed')
    let showFormulaCols = props.data.some(x => x.deal?.pricingType === 'Formula')
    let showPremiumCol = props.data.some(x => x.deal && x.deal.pricingType !== 'Fixed')
    let total = roundedSum<MovementDealDetail>(props.data, x => x.movementQuantity)

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell className={props.classes.alignRight}>{getPurchaseDetailTrad('reference')}</TableCell>
                    <TableCell className={props.classes.alignRight}>{getPurchaseDetailTrad('quantity')}</TableCell>
                    <TableCell className={props.classes.alignRight}>{getPurchaseDetailTrad('status')}</TableCell>
                    {showFormulaCols && <TableCell className={props.classes.alignRight}>{getPurchaseDetailTrad('pricingFrom')}</TableCell>}
                    {showFormulaCols && <TableCell className={props.classes.alignRight}>{getPurchaseDetailTrad('pricingTo')}</TableCell>}
                    {showFixedCols && <TableCell className={props.classes.alignRight}>{getPurchaseDetailTrad('fixedPriceDate')}</TableCell>}
                    {showFixedCols && <TableCell className={props.classes.alignRight}>{getPurchaseDetailTrad('fixedPrice')}</TableCell>}
                    {showTriggerCols && <TableCell className={props.classes.alignRight}>{getPurchaseDetailTrad('triggerPriceDate')}</TableCell>}
                    {showTriggerCols && <TableCell className={props.classes.alignRight}>{getPurchaseDetailTrad('triggerPrice')}</TableCell>}
                    {showPremiumCol && <TableCell className={props.classes.alignRight}>{getPurchaseDetailTrad('premium')}</TableCell>}
                    <TableCell></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {props.data.map((row, index) => (
                    <TableRow key={index}>
                        <TableCell className={props.classes.alignRight}>{row.movementReference}</TableCell>
                        <TableCell className={props.classes.alignRight}>{row.movementQuantity}</TableCell>
                        <TableCell className={props.classes.alignRight}> <StatusIcon movementStatus={row.movementStatus} classes={props.classes} /> </TableCell>
                        {showFormulaCols && <TableCell className={props.classes.alignRight}>{formatDate(row.deal?.pricingFrom, props.dateFormat)}</TableCell>}
                        {showFormulaCols && <TableCell className={props.classes.alignRight}>{formatDate(row.deal?.pricingTo, props.dateFormat)}</TableCell>}
                        {showFixedCols && <TableCell className={props.classes.alignRight}>{formatDate(row.deal?.fixedPriceDate, props.dateFormat)}</TableCell>}
                        {showFixedCols && <TableCell className={props.classes.alignRight}>{row.deal?.fixedPrice}</TableCell>}
                        {showTriggerCols && <TableCell className={props.classes.alignRight}>{formatDate(row.deal?.triggerPriceDate, props.dateFormat)}</TableCell>}
                        {showTriggerCols && <TableCell className={props.classes.alignRight}>{row.deal?.triggerPrice}</TableCell>}
                        {showPremiumCol && <TableCell className={props.classes.alignRight}>{row.deal?.pricingType === 'Fixed' ? '' : row.deal?.premium}</TableCell>}
                        <TableCell> {!row.deal && <ErrorOutlineOutlined className={props.classes.red} />} </TableCell>
                    </TableRow>
                ))}
                <TableRow>
                    <TableCell className={props.classes.totalCell + ' ' + props.classes.alignRight}>Total</TableCell>
                    <TableCell className={props.classes.totalCell + ' ' + props.classes.alignRight}>{total}</TableCell>
                    <TableCell colSpan={8}></TableCell>

                </TableRow>
            </TableBody>
        </Table>
    );
}

let cell: CSSProperties = dialogCell
let styles = () => createStyles({
    cell,
    title: { marginRight: '0.5em' },
    value: { fontWeight: 'lighter' },
    line: { ...defaultStyles.flexRow },
    alignRight: { textAlign: 'right' },
    totalCell: {
        fontWeight: 'bold'
    },
    red: { color: defaultColors.red.light.color },
})

let dialogRef: RefObject<{ open: (args: PurchaseDealDetailArgs) => void }> | null = null

export let purchaseDealDetailDialog = {
    open: (args: PurchaseDealDetailArgs) => {
        dialogRef?.current?.open(args)
    }
}

export let PurchaseDealDetailDialog = withStyles(styles)(PurchaseDetailDialog)